import { PermissionTypes, PermissionState } from "../types/permissionTypes";

const initialState: PermissionState = {
  constants: {
    data: null,
    status: "idle",
    error: null,
  },
  scopeOptions: {
    data: null,
    status: "idle",
    error: null,
  },
  scopePrograms: {
    data: null,
    status: "idle",
    error: null,
  },
  me: {
    data: null,
    status: "idle",
    error: null,
  },
  roles: {
    data: null,
    status: "idle",
    error: null,
  },
  upsertRole: {
    status: "idle",
    error: null,
  },
  deleteRole: {
    status: "idle",
    error: null,
  },
  changeRoleStatus: {
    status: "idle",
    error: null,
  },
  assignments: {
    data: null,
    status: "idle",
    error: null,
  },
  upsertAssignment: {
    status: "idle",
    error: null,
  },
  deleteAssignment: {
    status: "idle",
    error: null,
  },
  changeAssignmentStatus: {
    status: "idle",
    error: null,
  },
};

const permissionReducer = (state = initialState, action: any): PermissionState => {
  switch (action.type) {
    case PermissionTypes.GET_PERMISSION_CONSTANTS_REQUEST:
      return {
        ...state,
        constants: { ...state.constants, status: "loading", error: null },
      };

    case PermissionTypes.GET_PERMISSION_CONSTANTS_SUCCESS:
      return {
        ...state,
        constants: {
          data: action.payload?.data || null,
          status: "succeeded",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_CONSTANTS_FAILURE:
      return {
        ...state,
        constants: {
          ...state.constants,
          status: "failed",
          error: action.payload?.message || "Failed to fetch permission constants",
        },
      };

    case PermissionTypes.GET_PERMISSION_SCOPE_OPTIONS_REQUEST:
      return {
        ...state,
        scopeOptions: { ...state.scopeOptions, status: "loading", error: null },
      };

    case PermissionTypes.GET_PERMISSION_SCOPE_OPTIONS_SUCCESS:
      return {
        ...state,
        scopeOptions: {
          data: action.payload?.data || null,
          status: "succeeded",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_SCOPE_OPTIONS_FAILURE:
      return {
        ...state,
        scopeOptions: {
          ...state.scopeOptions,
          status: "failed",
          error: action.payload?.message || "Failed to fetch scope options",
        },
      };

    case PermissionTypes.GET_PERMISSION_SCOPE_PROGRAMS_REQUEST:
      return {
        ...state,
        scopePrograms: { ...state.scopePrograms, status: "loading", error: null },
      };

    case PermissionTypes.GET_PERMISSION_SCOPE_PROGRAMS_SUCCESS:
      return {
        ...state,
        scopePrograms: {
          data: action.payload?.data || null,
          status: "succeeded",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_SCOPE_PROGRAMS_FAILURE:
      return {
        ...state,
        scopePrograms: {
          ...state.scopePrograms,
          status: "failed",
          error: action.payload?.message || "Failed to fetch scope programs",
        },
      };

    case PermissionTypes.GET_PERMISSION_ME_REQUEST:
      return {
        ...state,
        me: { ...state.me, status: "loading", error: null },
      };

    case PermissionTypes.GET_PERMISSION_ME_SUCCESS:
      return {
        ...state,
        me: {
          data: action.payload?.data || null,
          status: "succeeded",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_ME_FAILURE:
      return {
        ...state,
        me: {
          data: null,
          status: "failed",
          error: action.payload?.message || "Failed to fetch user permissions",
        },
      };

    case PermissionTypes.GET_PERMISSION_ROLES_REQUEST:
      return {
        ...state,
        roles: {
          ...state.roles,
          status: "loading",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_ROLES_SUCCESS:
      return {
        ...state,
        roles: {
          data: action.payload?.data.roles || null,
          status: "succeeded",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_ROLES_FAILURE:
      return {
        ...state,
        roles: {
          data: null,
          status: "failed",
          error: action.payload?.message || "Failed to retrieve permission roles",
        },
      };

    case PermissionTypes.UPSERT_PERMISSION_ROLE_REQUEST:
      return {
        ...state,
        upsertRole: { status: "loading", error: null },
      };

    case PermissionTypes.UPSERT_PERMISSION_ROLE_SUCCESS: {
      const upsertedRole = action.payload?.data;
      if (!upsertedRole || !state.roles.data) {
        return { ...state, upsertRole: { status: "succeeded", error: null } };
      }
      const existingIndex = state.roles.data.findIndex(
        (r) => r.role_id === upsertedRole.role_id
      );
      let updatedRoles;
      if (existingIndex >= 0) {
        updatedRoles = [...state.roles.data];
        updatedRoles[existingIndex] = upsertedRole;
      } else {
        updatedRoles = [...state.roles.data, upsertedRole];
      }
      return {
        ...state,
        upsertRole: { status: "succeeded", error: null },
        roles: {
          ...state.roles,
          data: updatedRoles,
        },
      };
    }

    case PermissionTypes.UPSERT_PERMISSION_ROLE_FAILURE:
      return {
        ...state,
        upsertRole: {
          status: "failed",
          error: action.payload?.message || "Failed to save role",
        },
      };

    case PermissionTypes.DELETE_PERMISSION_ROLE_REQUEST:
      return {
        ...state,
        deleteRole: { status: "loading", error: null },
      };

    case PermissionTypes.DELETE_PERMISSION_ROLE_SUCCESS: {
      const deletedIds: string[] = action.payload?.data?.role_ids || [];
      return {
        ...state,
        deleteRole: { status: "succeeded", error: null },
        roles: {
          ...state.roles,
          data: deletedIds.length > 0 && state.roles.data
            ? state.roles.data.filter((r) => !deletedIds.includes(r.role_id))
            : state.roles.data,
        },
      };
    }

    case PermissionTypes.DELETE_PERMISSION_ROLE_FAILURE:
      return {
        ...state,
        deleteRole: {
          status: "failed",
          error: action.payload?.message || "Failed to delete role",
        },
      };

    case PermissionTypes.CHANGE_PERMISSION_ROLE_STATUS_REQUEST:
      return {
        ...state,
        changeRoleStatus: { status: "loading", error: null },
      };

    case PermissionTypes.CHANGE_PERMISSION_ROLE_STATUS_SUCCESS:
      return {
        ...state,
        changeRoleStatus: { status: "succeeded", error: null },
      };

    case PermissionTypes.CHANGE_PERMISSION_ROLE_STATUS_FAILURE:
      return {
        ...state,
        changeRoleStatus: {
          status: "failed",
          error: action.payload?.message || "Failed to change role status",
        },
      };

    case PermissionTypes.GET_PERMISSION_ASSIGNMENTS_REQUEST:
      return {
        ...state,
        assignments: {
          ...state.assignments,
          status: "loading",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_ASSIGNMENTS_SUCCESS:
      return {
        ...state,
        assignments: {
          data: action.payload?.data || null,
          status: "succeeded",
          error: null,
        },
      };

    case PermissionTypes.GET_PERMISSION_ASSIGNMENTS_FAILURE:
      return {
        ...state,
        assignments: {
          data: null,
          status: "failed",
          error: action.payload?.message || "Failed to retrieve permission assignments",
        },
      };

    case PermissionTypes.UPSERT_PERMISSION_ASSIGNMENT_REQUEST:
      return {
        ...state,
        upsertAssignment: { status: "loading", error: null },
      };

    case PermissionTypes.UPSERT_PERMISSION_ASSIGNMENT_SUCCESS: {
      const upserted = action.payload?.data;
      if (!upserted || !state.assignments.data) {
        return { ...state, upsertAssignment: { status: "succeeded", error: null } };
      }
      const existing = state.assignments.data.assignments;
      const idx = existing.findIndex((a) => a.assignment_id === upserted.assignment_id);
      let updated;
      if (idx >= 0) {
        updated = [...existing];
        updated[idx] = upserted;
      } else {
        updated = [...existing, upserted];
      }
      return {
        ...state,
        upsertAssignment: { status: "succeeded", error: null },
        assignments: {
          ...state.assignments,
          data: {
            ...state.assignments.data,
            assignments: updated,
            total_count: idx >= 0
              ? state.assignments.data.total_count
              : (state.assignments.data.total_count || existing.length) + 1,
          },
        },
      };
    }

    case PermissionTypes.UPSERT_PERMISSION_ASSIGNMENT_FAILURE:
      return {
        ...state,
        upsertAssignment: {
          status: "failed",
          error: action.payload?.message || "Failed to save assignment",
        },
      };

    case PermissionTypes.DELETE_PERMISSION_ASSIGNMENTS_REQUEST:
      return {
        ...state,
        deleteAssignment: { status: "loading", error: null },
      };

    case PermissionTypes.DELETE_PERMISSION_ASSIGNMENTS_SUCCESS: {
      const results = action.payload?.data?.results;
      if (!results || !state.assignments.data) {
        return { ...state, deleteAssignment: { status: "succeeded", error: null } };
      }
      const deletedIds = results
        .filter((r: any) => r.status === "deleted")
        .map((r: any) => r.assignment_id);
      const remainingAssignments = state.assignments.data.assignments.filter(
        (a) => !deletedIds.includes(a.assignment_id)
      );
      return {
        ...state,
        deleteAssignment: { status: "succeeded", error: null },
        assignments: {
          ...state.assignments,
          data: {
            ...state.assignments.data,
            assignments: remainingAssignments,
            total_count: (state.assignments.data.total_count || 0) - deletedIds.length,
          },
        },
      };
    }

    case PermissionTypes.DELETE_PERMISSION_ASSIGNMENTS_FAILURE:
      return {
        ...state,
        deleteAssignment: {
          status: "failed",
          error: action.payload?.message || "Failed to delete assignment",
        },
      };

    case PermissionTypes.CHANGE_PERMISSION_ASSIGNMENT_STATUS_REQUEST:
      return {
        ...state,
        changeAssignmentStatus: { status: "loading", error: null },
      };

    case PermissionTypes.CHANGE_PERMISSION_ASSIGNMENT_STATUS_SUCCESS:
      return {
        ...state,
        changeAssignmentStatus: { status: "succeeded", error: null },
      };

    case PermissionTypes.CHANGE_PERMISSION_ASSIGNMENT_STATUS_FAILURE:
      return {
        ...state,
        changeAssignmentStatus: {
          status: "failed",
          error: action.payload?.message || "Failed to change assignment status",
        },
      };

    default:
      return state;
  }
};

export default permissionReducer;
